/**
 * Admin Dashboard Page
 * Central hub for system administration with KPIs and quick links
 */

import { useEffect } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useSelector, useDispatch } from '@spcc/core'
import { useTranslation } from '@spcc/core'
import { isRtlLanguage } from '@spcc/portal-shared'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  cn,
} from '@spcc/uikit'
import {
  Building2,
  Users,
  CreditCard,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  ChevronRight,
  ChevronLeft,
  Shield,
  ArrowUpRight,
  Ban,
} from '@spcc/uikit'

// Domain imports
import { selectTenantsStats, selectPendingTenantsCount, tenantsActions } from '@spcc/domains/tenants'
import { selectComputedStats as selectUsersStats, usersActions } from '@spcc/domains/users'
import {
  selectComputedStats as selectAccountsStats,
  selectOverdueCount,
  customerAccountsActions,
} from '@spcc/domains/customer-accounts'

export const Route = createFileRoute('/admin/')({
  component: AdminDashboardPage,
})

// ====================================
// Constants
// ====================================
type TranslateFn = ReturnType<typeof useTranslation>['t']

function formatMoney(amount: number, currency = 'SAR'): string {
  return new Intl.NumberFormat('en-SA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

// ====================================
// Stats Card Component
// ====================================
interface StatsCardProps {
  title: string
  value: string | number
  icon: React.ReactNode
  variant?: 'default' | 'warning' | 'success' | 'danger' | 'info'
  subtitle?: string
  trend?: { value: number; label: string }
}

function StatsCard({ title, value, icon, variant = 'default', subtitle, trend }: StatsCardProps) {
  const variantStyles = {
    default: 'bg-card',
    warning: 'bg-amber-50 dark:bg-amber-900/20',
    success: 'bg-green-50 dark:bg-green-900/20',
    danger: 'bg-red-50 dark:bg-red-900/20',
    info: 'bg-blue-50 dark:bg-blue-900/20',
  }

  const iconBgStyles = {
    default: 'bg-primary/10',
    warning: 'bg-amber-100 dark:bg-amber-800/30',
    success: 'bg-green-100 dark:bg-green-800/30',
    danger: 'bg-red-100 dark:bg-red-800/30',
    info: 'bg-blue-100 dark:bg-blue-800/30',
  }

  const gradientColors = {
    default: 'from-blue-500 to-blue-600',
    warning: 'from-amber-500 to-amber-600',
    success: 'from-green-500 to-green-600',
    danger: 'from-red-500 to-red-600',
    info: 'from-blue-500 to-blue-600',
  }

  return (
    <Card className={cn('border border-border/50', variantStyles[variant])}>
      <CardContent className="flex items-center gap-4 p-5">
        <div className={cn('rounded-xl p-3', iconBgStyles[variant])}>{icon}</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-muted-foreground truncate">{title}</p>
          <p
            className={cn(
              'text-2xl font-bold bg-gradient-to-r bg-clip-text text-transparent',
              gradientColors[variant]
            )}
          >
            {value}
          </p>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          {trend && (
            <div className="flex items-center gap-1 mt-1">
              <TrendingUp className="w-3 h-3 text-green-600" />
              <span className="text-xs text-green-600">+{trend.value}%</span>
              <span className="text-xs text-muted-foreground">{trend.label}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// ====================================
// Quick Link Card Component
// ====================================
interface QuickLinkCardProps {
  title: string
  description: string
  icon: React.ReactNode
  href: string
  stats?: { label: string; value: string | number; variant?: 'default' | 'warning' | 'danger' }[]
  isRTL: boolean
}

function QuickLinkCard({ title, description, icon, href, stats, isRTL }: QuickLinkCardProps) {
  const ChevronIcon = isRTL ? ChevronLeft : ChevronRight

  return (
    <Link to={href} className="block group">
      <Card className="h-full transition-colors duration-300 hover:shadow-lg hover:border-primary/50 border border-border/50">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="rounded-xl bg-primary/10 p-3">{icon}</div>
            <ArrowUpRight className="w-5 h-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <CardTitle className="text-lg group-hover:text-primary transition-colors mt-4">
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        {stats && stats.length > 0 && (
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-2">
              {stats.map((stat, index) => (
                <Badge
                  key={index}
                  variant={stat.variant === 'warning' ? 'outline' : stat.variant === 'danger' ? 'destructive' : 'secondary'}
                  className={cn(
                    'text-xs',
                    stat.variant === 'warning' && 'border-amber-500 text-amber-600'
                  )}
                >
                  {stat.value} {stat.label}
                </Badge>
              ))}
            </div>
          </CardContent>
        )}
      </Card>
    </Link>
  )
}

// ====================================
// Alert Card Component
// ====================================
interface AlertItem {
  id: string
  type: 'warning' | 'danger' | 'info'
  title: string
  description: string
  link?: string
}

interface AlertCardProps {
  alerts: AlertItem[]
  isRTL: boolean
}

function AlertCard({ alerts, isRTL }: AlertCardProps) {
  const { t } = useTranslation()
  const ChevronIcon = isRTL ? ChevronLeft : ChevronRight

  if (alerts.length === 0) return null

  const alertStyles = {
    warning: 'border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-900/20',
    danger: 'border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-900/20',
    info: 'border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-900/20',
  }

  const iconStyles = {
    warning: 'text-amber-600',
    danger: 'text-red-600',
    info: 'text-blue-600',
  }

  return (
    <Card className="border border-border/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          {t('admin.dashboard.alerts', 'Alerts & Notifications')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className={cn('rounded-lg border p-3', alertStyles[alert.type])}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-2">
                <AlertTriangle className={cn('w-4 h-4 mt-0.5', iconStyles[alert.type])} />
                <div>
                  <p className="font-medium text-sm">{alert.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{alert.description}</p>
                </div>
              </div>
              {alert.link && (
                <Link to={alert.link} className="text-primary hover:underline">
                  <ChevronIcon className="w-4 h-4" />
                </Link>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

// ====================================
// Main Dashboard Component
// ====================================
function AdminDashboardPage() {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const isRTL = isRtlLanguage(i18n.language)

  // Redux selectors
  const tenantsStats = useSelector(selectTenantsStats)
  const pendingTenantsCount = useSelector(selectPendingTenantsCount)
  const usersStats = useSelector(selectUsersStats)
  const accountsStats = useSelector(selectAccountsStats)
  const overdueAccountsCount = useSelector(selectOverdueCount)

  // Fetch data on mount
  useEffect(() => {
    dispatch(tenantsActions.fetchTenants())
    dispatch(usersActions.fetchUsers({}))
    dispatch(customerAccountsActions.fetchAccounts({}))
  }, [dispatch])

  // Build alerts list
  const alerts: AlertItem[] = []

  if (pendingTenantsCount > 0) {
    alerts.push({
      id: 'pending-tenants',
      type: 'warning',
      title: t('admin.dashboard.pendingTenantsAlert', 'Pending Tenant Approvals'),
      description: t(
        'admin.dashboard.pendingTenantsDesc',
        '{{count}} tenant(s) awaiting approval',
        { count: pendingTenantsCount }
      ),
      link: '/admin/tenants',
    })
  }

  if (usersStats.blocked > 0) {
    alerts.push({
      id: 'blocked-users',
      type: 'danger',
      title: t('admin.dashboard.blockedUsersAlert', 'Blocked Users'),
      description: t(
        'admin.dashboard.blockedUsersDesc',
        '{{count}} user(s) are currently blocked',
        { count: usersStats.blocked }
      ),
      link: '/admin/users',
    })
  }

  if (overdueAccountsCount > 0) {
    alerts.push({
      id: 'overdue-accounts',
      type: 'danger',
      title: t('admin.dashboard.overdueAccountsAlert', 'Overdue Accounts'),
      description: t(
        'admin.dashboard.overdueAccountsDesc',
        '{{count}} account(s) have overdue balances',
        { count: overdueAccountsCount }
      ),
      link: '/admin/customer-accounts',
    })
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Page Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Shield className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">
            {t('admin.dashboard.title', 'Admin Dashboard')}
          </h1>
        </div>
        <p className="text-muted-foreground">
          {t('admin.dashboard.subtitle', 'System overview and management')}
        </p>
      </div>

      {/* KPI Stats Row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatsCard
          title={t('admin.dashboard.totalTenants', 'Total Tenants')}
          value={tenantsStats.total}
          icon={<Building2 className="h-5 w-5 text-primary" />}
          variant="default"
          subtitle={`${tenantsStats.active} ${t('admin.dashboard.active', 'active')}`}
        />
        <StatsCard
          title={t('admin.dashboard.totalUsers', 'Total Users')}
          value={usersStats.total}
          icon={<Users className="h-5 w-5 text-blue-600" />}
          variant="info"
          subtitle={`${usersStats.active} ${t('admin.dashboard.active', 'active')}`}
        />
        <StatsCard
          title={t('admin.dashboard.totalCredit', 'Total Credit Limit')}
          value={formatMoney(accountsStats.totalCreditLimit?.amount ?? 0)}
          icon={<TrendingUp className="h-5 w-5 text-green-600" />}
          variant="success"
        />
        <StatsCard
          title={t('admin.dashboard.totalOverdue', 'Total Overdue')}
          value={formatMoney(accountsStats.totalOverdue?.amount ?? 0)}
          icon={<AlertTriangle className="h-5 w-5 text-red-600" />}
          variant="danger"
          subtitle={`${overdueAccountsCount} ${t('admin.dashboard.accounts', 'accounts')}`}
        />
      </div>

      {/* Quick Actions Grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <QuickLinkCard
          title={t('admin.dashboard.tenantManagement', 'Tenant Management')}
          description={t('admin.dashboard.tenantManagementDesc', 'Manage organizations and their access')}
          icon={<Building2 className="h-6 w-6 text-primary" />}
          href="/admin/tenants"
          isRTL={isRTL}
          stats={[
            { label: t('admin.dashboard.active', 'active'), value: tenantsStats.active },
            pendingTenantsCount > 0
              ? { label: t('admin.dashboard.pending', 'pending'), value: pendingTenantsCount, variant: 'warning' }
              : null,
            tenantsStats.suspended > 0
              ? { label: t('admin.dashboard.suspended', 'suspended'), value: tenantsStats.suspended, variant: 'danger' }
              : null,
          ].filter(Boolean) as QuickLinkCardProps['stats']}
        />

        <QuickLinkCard
          title={t('admin.dashboard.userManagement', 'User Management')}
          description={t('admin.dashboard.userManagementDesc', 'Manage users across all tenants')}
          icon={<Users className="h-6 w-6 text-blue-600" />}
          href="/admin/users"
          isRTL={isRTL}
          stats={[
            { label: t('admin.dashboard.active', 'active'), value: usersStats.active },
            usersStats.invited > 0
              ? { label: t('admin.dashboard.invited', 'invited'), value: usersStats.invited }
              : null,
            usersStats.blocked > 0
              ? { label: t('admin.dashboard.blocked', 'blocked'), value: usersStats.blocked, variant: 'danger' }
              : null,
          ].filter(Boolean) as QuickLinkCardProps['stats']}
        />

        <QuickLinkCard
          title={t('admin.dashboard.customerAccounts', 'Customer Accounts')}
          description={t('admin.dashboard.customerAccountsDesc', 'Manage financial accounts and credit')}
          icon={<CreditCard className="h-6 w-6 text-green-600" />}
          href="/admin/customer-accounts"
          isRTL={isRTL}
          stats={[
            { label: t('admin.dashboard.active', 'active'), value: accountsStats.active },
            accountsStats.suspended > 0
              ? { label: t('admin.dashboard.suspended', 'suspended'), value: accountsStats.suspended, variant: 'warning' }
              : null,
            overdueAccountsCount > 0
              ? { label: t('admin.dashboard.overdue', 'overdue'), value: overdueAccountsCount, variant: 'danger' }
              : null,
          ].filter(Boolean) as QuickLinkCardProps['stats']}
        />
      </div>

      {/* Alerts Section */}
      {alerts.length > 0 && <AlertCard alerts={alerts} isRTL={isRTL} />}

      {/* System Status */}
      <Card className="border border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            {t('admin.dashboard.systemStatus', 'System Status')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-sm text-muted-foreground">
                {t('admin.dashboard.apiStatus', 'API Services')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-sm text-muted-foreground">
                {t('admin.dashboard.authStatus', 'Authentication')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-sm text-muted-foreground">
                {t('admin.dashboard.dbStatus', 'Database')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-sm text-muted-foreground">
                {t('admin.dashboard.cacheStatus', 'Cache')}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
